import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { mkt } from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone, TrendingUp, Users, FileText, Sparkles, ArrowLeft, LogOut, Plus,
  Target, Zap, BarChart3, Send,
} from "lucide-react";

export default function Marketing() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [campanhas, setCampanhas] = useState([]);
  const [leads, setLeads] = useState([]);
  const [conteudos, setConteudos] = useState([]);
  const [showIACopy, setShowIACopy] = useState(false);

  useEffect(() => { load(); }, [tab]);
  const load = async () => {
    try {
      if (tab === "dashboard") setDash((await mkt.dashboard()).data);
      if (tab === "campanhas") setCampanhas((await mkt.campanhas()).data);
      if (tab === "leads") setLeads((await mkt.leads()).data);
      if (tab === "conteudos") setConteudos((await mkt.conteudos()).data);
    } catch { toast.error("Erro ao carregar"); }
  };
  const fmt = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const num = (v) => (v ?? 0).toLocaleString("pt-BR");

  return (
    <div className="min-h-screen bg-[#0A0B1A] flex flex-col">
      <header className="bg-gradient-to-r from-[#1A1D42] to-[#252952] border-b border-[#B28F4B]/25 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-5 py-3 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#0A0B1A] border-2 border-[#B28F4B] flex items-center justify-center">
            <Megaphone size={20} className="text-[#B28F4B]" />
          </div>
          <div className="flex-1">
            <div className="text-[#B28F4B] font-bold font-heading text-lg">SOEASY · Marketing & Growth</div>
            <div className="text-white/60 text-[10px] uppercase tracking-widest">Institucional · Captação · Branding · Presença Digital · LGPD-compliant</div>
          </div>
          <button onClick={() => setShowIACopy(true)} data-testid="btn-ia-mkt" className="px-3 py-1.5 rounded-md bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] text-xs font-bold uppercase tracking-widest flex items-center gap-1.5"><Sparkles size={12}/> Copy IA</button>
          <button onClick={() => navigate("/setores")} data-testid="btn-back-setores" className="px-3 py-1.5 rounded-md border border-[#B28F4B]/40 text-white text-xs font-bold uppercase tracking-widest">Setores</button>
          <button onClick={() => { logout(); navigate("/login"); }} className="px-3 py-1.5 rounded-md bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold uppercase tracking-widest">Sair</button>
        </div>
        <div className="max-w-[1600px] mx-auto px-5 flex gap-1 overflow-x-auto">
          {[
            { id: "dashboard", label: "Central de Growth", icon: BarChart3 },
            { id: "campanhas", label: "Campanhas", icon: Target },
            { id: "leads", label: "Leads & Funil", icon: Users },
            { id: "conteudos", label: "Conteúdos", icon: FileText },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-mkt-${t.id}`}
              className={`px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest border-b-2 flex items-center gap-1.5 ${tab === t.id ? "border-[#B28F4B] text-[#B28F4B]" : "border-transparent text-white/60 hover:text-white"}`}>
              <t.icon size={13}/> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto p-5 fade-up">
        {tab === "dashboard" && dash && <DashMkt dash={dash} fmt={fmt} num={num}/>}
        {tab === "campanhas" && <Campanhas items={campanhas} fmt={fmt} num={num} reload={load}/>}
        {tab === "leads" && <Leads items={leads} reload={load}/>}
        {tab === "conteudos" && <Conteudos items={conteudos} num={num}/>}
      </main>

      {showIACopy && <ModalIACopy onClose={() => setShowIACopy(false)}/>}
    </div>
  );
}

function DashMkt({ dash, fmt, num }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiM title="Campanhas Ativas" value={dash.campanhas_ativas} sub={`de ${dash.campanhas_total} total`} color="#B28F4B" icon={Target}/>
        <KpiM title="Leads Captados" value={num(dash.leads_total)} sub={`${dash.taxa_conversao}% conversão`} color="#22D3EE" icon={Users}/>
        <KpiM title="CPL Médio" value={fmt(dash.cpl)} sub={`CPA ${fmt(dash.cpa)}`} color="#F59E0B" icon={Zap}/>
        <KpiM title="Alcance Total" value={num(dash.alcance_total)} sub={`${num(dash.engajamento_total)} engajamentos`} color="#EC4899" icon={TrendingUp}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
          <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Investimento em Mídia</div>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Orçamento</span><span className="text-[#B28F4B] font-mono font-bold">{fmt(dash.orcamento_total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Gasto até agora</span><span className="text-emerald-400 font-mono font-bold">{fmt(dash.gasto_total)}</span>
            </div>
            <div className="h-2 bg-[#0A0B1A] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#B28F4B] to-[#D4AF37]" style={{width: `${dash.gasto_pct}%`}}/>
            </div>
            <div className="text-right text-xs text-[#B28F4B] font-bold">{dash.gasto_pct}% consumido</div>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#B28F4B]/20">
            <MiniStat label="Impressões" value={num(dash.impressoes)}/>
            <MiniStat label="Cliques" value={num(dash.cliques)}/>
            <MiniStat label="CTR" value={`${dash.ctr}%`}/>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
          <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Leads por Canal</div>
          <div className="space-y-2">
            {Object.entries(dash.leads_por_canal || {}).map(([k, v]) => {
              const max = Math.max(...Object.values(dash.leads_por_canal), 1);
              return (
                <div key={k}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-white uppercase">{k.replace(/_/g, " ")}</span><span className="text-[#B28F4B] font-mono font-bold">{v}</span></div>
                  <div className="h-1.5 bg-[#0A0B1A] rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500" style={{width: `${v/max*100}%`}}/></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
        <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Funil de Leads</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {["novo","qualificado","em_negociacao","convertido","perdido"].map((s, i) => {
            const cores = ["#3B82F6","#F59E0B","#8B5CF6","#10B981","#EF4444"];
            return (
              <div key={s} className="p-3 rounded-lg border" style={{borderColor: cores[i]+"40", background: cores[i]+"10"}}>
                <div className="text-[9px] font-bold uppercase tracking-widest" style={{color: cores[i]}}>{s.replace(/_/g, " ")}</div>
                <div className="text-2xl font-mono font-bold text-white mt-1">{dash.leads_por_status?.[s] ?? 0}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function KpiM({ title, value, sub, color, icon: Icon }) {
  return (
    <div className="bg-gradient-to-br from-[#131532] to-transparent border border-[#B28F4B]/25 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#A1A4C7]">{title}</div>
        <Icon size={16} style={{color}}/>
      </div>
      <div className="font-mono font-bold text-2xl" style={{color}}>{value}</div>
      <div className="text-[9px] text-[#6B6E8F] mt-0.5 uppercase tracking-widest">{sub}</div>
    </div>
  );
}
function MiniStat({label, value}) { return (
  <div className="text-center">
    <div className="text-[9px] text-[#6B6E8F] uppercase tracking-widest">{label}</div>
    <div className="text-white text-sm font-mono font-bold mt-0.5">{value}</div>
  </div>
);}

function Campanhas({ items, fmt, num, reload }) {
  return (
    <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#0A0B1A]/60 border-b border-[#B28F4B]/25 text-left text-[9px] font-bold uppercase tracking-[0.2em] text-[#B28F4B]">
          <tr><th className="p-3">Campanha</th><th className="p-3">Canal</th><th className="p-3 text-right">Orçamento</th><th className="p-3 text-right">Gasto</th><th className="p-3 text-right">Leads</th><th className="p-3 text-right">Conversões</th><th className="p-3">Status</th></tr>
        </thead>
        <tbody>
          {items.map(c => (
            <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
              <td className="p-3 text-white text-xs font-semibold">{c.nome}</td>
              <td className="p-3 text-[#A1A4C7] text-[10px] uppercase font-mono">{c.canal}</td>
              <td className="p-3 text-right text-white/70 font-mono">{fmt(c.orcamento)}</td>
              <td className="p-3 text-right text-emerald-400 font-mono">{fmt(c.gasto)}</td>
              <td className="p-3 text-right text-cyan-400 font-mono">{num(c.leads_gerados)}</td>
              <td className="p-3 text-right text-[#B28F4B] font-mono">{num(c.conversoes)}</td>
              <td className="p-3"><span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${c.status==="ativa"?"bg-emerald-500/15 text-emerald-400":c.status==="planejada"?"bg-blue-500/15 text-blue-400":"bg-slate-500/15 text-slate-400"}`}>{c.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Leads({ items, reload }) {
  return (
    <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#0A0B1A]/60 border-b border-[#B28F4B]/25 text-left text-[9px] font-bold uppercase tracking-[0.2em] text-[#B28F4B]">
          <tr><th className="p-3">Nome</th><th className="p-3">Empresa</th><th className="p-3">Cargo</th><th className="p-3">Interesse</th><th className="p-3">Canal</th><th className="p-3 text-center">Score</th><th className="p-3">Status</th></tr>
        </thead>
        <tbody>
          {items.map(l => (
            <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02]">
              <td className="p-3 text-white text-xs font-bold">{l.nome}</td>
              <td className="p-3 text-[#A1A4C7] text-xs">{l.empresa}</td>
              <td className="p-3 text-[#A1A4C7] text-xs">{l.cargo}</td>
              <td className="p-3 text-white/70 text-xs">{l.interesse}</td>
              <td className="p-3 text-[#A1A4C7] text-[10px] uppercase">{l.canal_origem}</td>
              <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${l.score>=80?"bg-emerald-500/20 text-emerald-400":l.score>=50?"bg-amber-500/20 text-amber-400":"bg-red-500/20 text-red-400"}`}>{l.score}</span></td>
              <td className="p-3"><span className="px-2 py-0.5 rounded bg-[#B28F4B]/15 text-[#B28F4B] text-[9px] font-bold uppercase tracking-widest">{l.status.replace(/_/g," ")}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Conteudos({ items, num }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map(c => (
        <div key={c.id} className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest bg-[#B28F4B]/15 text-[#B28F4B]">{c.tipo}</span>
            <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest bg-cyan-500/15 text-cyan-400">{c.canal}</span>
          </div>
          <h4 className="text-white font-heading text-sm font-bold mb-2 line-clamp-2">{c.titulo}</h4>
          <div className={`text-[10px] uppercase tracking-widest font-bold mb-3 ${c.status==="publicado"?"text-emerald-400":c.status==="aprovado"?"text-cyan-400":"text-amber-400"}`}>● {c.status.replace(/_/g," ")}</div>
          {c.status === "publicado" && (
            <div className="grid grid-cols-4 gap-1 pt-3 border-t border-[#B28F4B]/20">
              <MiniStat label="Alcance" value={num(c.alcance)}/>
              <MiniStat label="♥" value={num(c.curtidas)}/>
              <MiniStat label="↗" value={num(c.compartilhamentos)}/>
              <MiniStat label="💬" value={num(c.comentarios)}/>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ModalIACopy({ onClose }) {
  const [tema, setTema] = useState("");
  const [canal, setCanal] = useState("instagram");
  const [tom, setTom] = useState("profissional");
  const [copy, setCopy] = useState(null);
  const [loading, setLoading] = useState(false);
  const gerar = async () => {
    if (!tema) return toast.error("Informe o tema");
    setLoading(true);
    try {
      const r = await mkt.iaCopy(tema, canal, tom);
      setCopy(r.data.parsed || { headline: r.data.raw });
    } catch { toast.error("Erro na IA"); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="bg-[#131532] border border-[#B28F4B]/40 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-[#B28F4B] font-heading text-lg font-bold mb-4 flex items-center gap-2"><Sparkles size={18}/> Gerador de Copy IA · Claude 4.6</h3>
        <div className="space-y-3">
          <input placeholder="Tema (ex: promoção portaria 24h para condomínios)" value={tema} onChange={e=>setTema(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <select value={canal} onChange={e=>setCanal(e.target.value)} className="px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm">
              <option value="instagram">Instagram</option><option value="linkedin">LinkedIn</option><option value="facebook">Facebook</option><option value="email">E-mail</option><option value="google">Google Ads</option>
            </select>
            <select value={tom} onChange={e=>setTom(e.target.value)} className="px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm">
              <option value="profissional">Profissional</option><option value="institucional">Institucional</option><option value="urgente">Urgente</option><option value="descontraido">Descontraído</option>
            </select>
          </div>
          <button onClick={gerar} disabled={loading} data-testid="btn-gerar-copy" className="w-full py-2.5 rounded-lg bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50">
            <Send size={13}/> {loading ? "Gerando..." : "Gerar Copy"}
          </button>
          {copy && (
            <div className="mt-4 space-y-2 p-3 bg-[#0A0B1A] rounded-lg border border-[#B28F4B]/25">
              {copy.headline && <div><div className="text-[9px] font-bold uppercase tracking-widest text-[#B28F4B] mb-1">Headline</div><div className="text-white text-sm font-bold">{copy.headline}</div></div>}
              {copy.subheadline && <div><div className="text-[9px] font-bold uppercase tracking-widest text-[#B28F4B] mb-1">Sub-headline</div><div className="text-white/80 text-xs">{copy.subheadline}</div></div>}
              {copy.body && <div><div className="text-[9px] font-bold uppercase tracking-widest text-[#B28F4B] mb-1">Corpo</div><div className="text-white/80 text-xs whitespace-pre-wrap">{copy.body}</div></div>}
              {copy.cta && <div><div className="text-[9px] font-bold uppercase tracking-widest text-[#B28F4B] mb-1">CTA</div><div className="text-emerald-400 text-sm font-bold">{copy.cta}</div></div>}
              {copy.hashtags && <div className="flex flex-wrap gap-1">{copy.hashtags.map((h, i) => <span key={i} className="px-2 py-0.5 rounded bg-cyan-500/15 text-cyan-400 text-[10px]">{h.startsWith("#")?h:"#"+h}</span>)}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
